import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit, SimpleChanges, OnDestroy, OnChanges } from '@angular/core';
import { EntityConfigInterface } from '@rappider/rappider-components';
import { AiCrudViewComponent } from '../ai-crud-view/ai-crud-view.component';
import { EntityService } from '../../services/entity.service';
import { ProjectModelControllerService } from 'libs/rappider-sdk/src/lib/services/project-model-controller.service';
import { OpenAiDataModelResponseDto, ProjectModel, ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { getProjectModelsWithFields } from 'libs/shared/src/lib/state-selectors/get-models-with-fields-selector';
import { EntityMetabaseService } from '../../services/entity-metabase.service';
import { LoopbackCrudApiService } from '../../services/loopback-crud-api.service';
import { NotificationService } from '@rappider/services';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';

import { RappiderAiInputModalComponent } from '@rappider/rappider-components';

import * as lodash from 'lodash';

import { BrowserStorageManagementService } from
  'libs/shared/src/lib/services/browser-storage-management-service/browser-storage-management.service';

import { StoreKeys } from '@rappider/services';
import { SyncRemoteDataWithKey } from 'apps/rappider/src/app/state/app.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { FullPaths } from '@rappider/shared/definitions';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';

// Grid Builder Config Interface
export interface GridBuilderConfig {
  columns?: Array<Record<string, unknown>>;
  sorting?: Record<string, unknown>;
  filtering?: Record<string, unknown>;
  pagination?: Record<string, unknown>;
  pageSize?: number;
  searchable?: boolean;
  [key: string]: unknown;
}

@Component({
  selector: 'rappider-project-model-entities',
  standalone: true,
  imports: [
    CommonModule,
    AiCrudViewComponent,
    RappiderAiInputModalComponent,
    NzButtonModule,
    NzToolTipModule
  ],
  templateUrl: './project-model-entities.component.html',
  styleUrls: ['./project-model-entities.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectModelEntitiesComponent implements OnInit, OnDestroy, OnChanges {

  @Input() projectModel: ProjectModelWithRelations | undefined; // Optional input to display a specific data table

  subscriptions: Subscription[] = [];
  activeEntityConfig: EntityConfigInterface | undefined;
  activeEntityItem: any;

  entityConfigs: { [key: string]: EntityConfigInterface } = {};
  entityConfigsAsArray: EntityConfigInterface[] = []; // Object.values(this.entityConfigs);

  entityData: Record<string, any[]> = {};
  projectModels: ProjectModelWithRelations[] | undefined;

  /* Show Create New Model with AI Dialog */
  createNewModelWithAIDialogId = '';

  /* Add Model with AI Card */
  addModelWithAiCard: EntityConfigInterface = {
    id: 'add-data-model-with-ai',
    type: 'action',
    name: 'add-data-model-with-ai',
    iconName: 'fa fa-solid fa-sparkles',
    title: 'Add Data Table',
    description: 'Click here to add a new data table using AI assistance. Rapider is going to recommend the best fields for your data table and it will deploy your database with your new table.',
    config: undefined
  };
  recentGridConfigUpdate: string | null = null;


  constructor(
    private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private loopbackCrudApiService: LoopbackCrudApiService,
    private entityService: EntityService,
    private entityMetabaseService: EntityMetabaseService,
    private projectModelControllerService: ProjectModelControllerService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private notificationService: NotificationService
  ) { }

  // eslint-disable-next-line @typescript-eslint/no-empty-function

  ngOnInit() {
    this.subscribeToData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.projectModel && changes.projectModel.currentValue && this.projectModel) {
      if (this.projectModel?.dataTableName) {
        this.activeEntityConfig = this.entityConfigs[this.projectModel.dataTableName];
      } else {
        this.activeEntityConfig = undefined;
      }
      if (!this.subscriptions.length) {
        this.subscribeToData();
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.getProjectModelsByProjectId()
    ];
    this.changeDetectorRef.markForCheck();
  }

  getProjectModelsByProjectId() {
    return this.store.select(<any>getProjectModelsWithFields).subscribe(projectModels => {
      this.projectModels = projectModels;

      // Check if this update is due to a recent grid config update we initiated
      if (this.recentGridConfigUpdate) {
        console.log('Skipping entity config regeneration due to recent grid config update');
        // Clear the flag after a short delay to allow for subsequent legitimate updates
        setTimeout(() => {
          this.recentGridConfigUpdate = null;
        }, 1000);
        return;
      }

      // if there is a projectModel provided as an input, just display it
      if (this.projectModel && this.projectModels?.length) {
        const providedProjectModel = this.projectModels.find(m => m.id === this.projectModel?.id);
        if (providedProjectModel) {
          this.generateEntityConfigForOneProjectModel(providedProjectModel);
        }
      } else {
        // if no projectModel is provided, generate configs for all project models
        this.generateEntityConfigs();
      }

    });
  }

  generateEntityConfigs() {
    // filter blacklisted project models, we dont display low-code created models as they are not user's data source related models
    // e.g.: User and UiMenuItem

    // System models that we don't want to display
    const blacklistedModels = ['User', 'UiMenuItem', 'Role', 'RoleMapping', 'Permission'];
    // Filter out blacklisted models
    const projectEntityModels = this.projectModels?.filter(model => !blacklistedModels.includes(model.name)) || [];
    // order models by name
    const sortedProjectEntityModels = [...projectEntityModels].sort((a, b) => a.name.localeCompare(b.name));
    // Use .reduce() to build a new object immutably instead of mutating the existing one.
    const newEntityConfigs = sortedProjectEntityModels?.reduce((acc, projectModel) => {
      const entityMetaConfig = this.entityMetabaseService.generateConfig(projectModel);
      entityMetaConfig.dataFetchedFrom = 'loopback-root-entity-api'; // Default value
      // Add the new config to our temporary object
      acc[projectModel.dataTableName] = this.entityService.generateEntityConfig(entityMetaConfig);
      return acc;
    }, {} as { [key: string]: EntityConfigInterface });

    // Create a corresponding entityData object
    const newEntityData = this.projectModels?.reduce((acc, projectModel) => {
      acc[projectModel.dataTableName] = [] as Array<Record<string, unknown>>;
      return acc;
    }, {} as Record<string, Array<Record<string, unknown>>>);

    /* Add Ai card */
    this.entityConfigs['add-data-model-with-ai'] = this.addModelWithAiCard;

    // add model configs
    // This is the step that triggers change detection in the child component.
    this.entityConfigs = { ...this.entityConfigs, ...newEntityConfigs || {} };

    this.entityData = newEntityData || {};

    this.entityConfigsAsArray = Object.values(this.entityConfigs);
    this.changeDetectorRef.markForCheck();
  }

  generateEntityConfigForOneProjectModel(projectModel: ProjectModelWithRelations) {
    const entityMetaConfig = this.entityMetabaseService.generateConfig(projectModel);
    entityMetaConfig.dataFetchedFrom = 'loopback-root-entity-api'; // Default value

    const dataTableName = projectModel.dataTableName;
    const newConfig = this.entityService.generateEntityConfig(entityMetaConfig);

    // Create a new object by spreading the old one and adding the new property.
    this.entityConfigs = {
      ...this.entityConfigs,
      [dataTableName]: newConfig
    };

    this.entityData = {
      ...this.entityData,
      [dataTableName]: [] as Array<Record<string, unknown>>
    };

    this.entityConfigsAsArray = Object.values(this.entityConfigs);
    this.activeEntityConfig = this.entityConfigs[dataTableName];
    this.changeDetectorRef.markForCheck();
  }

  onEntitySelected(entity: EntityConfigInterface) {
    console.log('Entity selected:', entity);
    // entity data is fetched by crud-view component
    this.activeEntityConfig = entity;
    this.changeDetectorRef.markForCheck();
  }

  onEntityItemSelected(item: Record<string, unknown>) {
    console.log('Entity item selected:', item);
    this.activeEntityItem = item;
    this.changeDetectorRef.markForCheck();
  }

  onActionCardClicked(cardData: any) {
    if (cardData?.id === 'add-data-model-with-ai') {
      // redirect to add new data model AI page
      this.router.navigate([FullPaths.GenerateDataModelWithAi]);
      // this.createNewModelWithAIDialogId = new Date().getTime().toString(); // Generate a unique ID for the dialog
    }
    console.log('Action card clicked:', cardData);
  }

  /* creates a project model */
  createProjectModelWithAi(aiDescriptionText: string) {
    if (aiDescriptionText?.trim()) {
      const params = {
        body: {
          message: aiDescriptionText?.trim()
        }
      };

      this.projectModelControllerService.generateByAI(params).subscribe(
        (projectModel: OpenAiDataModelResponseDto) => {
          console.log(projectModel);
          this.store.dispatch(new SyncRemoteDataWithKey({ storeKey: StoreKeys.ProjectModel }));
        },
        error => {
          console.log(error);
        }
      );
    }
  }

  onJoyrideDoneButtonClick() {
    this.browserStorageManagementService.setLocalStorageItem('tour', 'stop');
  }

  /**
   * Handles grid builder configuration saved event from AI CRUD view
   * @param gridConfig The grid configuration object
   */
  onGridBuilderConfigSaved(gridConfig: GridBuilderConfig) {
    this.handleGridBuilderConfig(gridConfig);
  }

  /**
   * Handles list grid visibility columns changes
   * @param visibilityColumns The updated visibility columns array
   */
  onListGridVisibilityColumnsChanged(visibilityColumns: string[]) {
    if (!visibilityColumns || !this.activeEntityConfig?.name) {
      return;
    }

    this.recentGridConfigUpdate = this.activeEntityConfig.name;

    const projectModel = this.projectModels?.find(model =>
      model.dataTableName === this.activeEntityConfig?.name
    );

    const existingGridConfig = projectModel?.gridConfig || {};
    const mergedGridConfig = {
      ...existingGridConfig,
      visibilityColumns: visibilityColumns,
      lastVisibilityColumnsUpdate: new Date().toISOString()
    };

    this.store.dispatch(new ProjectModelActions.UpdateProjectModelGridConfig({
      projectModelId: projectModel?.id || '',
      gridConfig: mergedGridConfig
    }));
  }

  /**
   * Grid builder configuration
   * @param gridConfig The grid configuration to apply
   */
  handleGridBuilderConfig(gridConfig: GridBuilderConfig): void {
    if (!gridConfig || !this.activeEntityConfig?.name) {
      return;
    }

    this.recentGridConfigUpdate = this.activeEntityConfig.name;

    const projectModel = this.projectModels?.find(model =>
      model.dataTableName === this.activeEntityConfig?.name
    );

    if (projectModel) {
      this.store.dispatch(new ProjectModelActions.UpdateProjectModelGridConfig({
        projectModelId: projectModel.id,
        gridConfig: gridConfig
      }));
    }
  }

  setQueryParameters(paramsToUpdate: { [key: string]: string | null | undefined }) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: paramsToUpdate,
      queryParamsHandling: 'merge',
    });
  }

  onUpdateDataTableSchemaButtonClicked() {
    this.router.navigate([FullPaths.DataModels]);
  }

  onDbDiagramButtonClicked() {
    this.router.navigate([FullPaths.DatabaseDiagram]);
  }

  onDbHomeButtonClicked() {
    this.activeEntityConfig = undefined;
    this.setQueryParameters({ entity: null, 'viewMode': 'list' });
  }
}
